import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import RowActions, {
  editAction,
  deleteAction,
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import { FormModal } from "../../components/Form/FormKit";
import AppointmentForm from "./AppointmentForm";
import AppointmentDetail from "./AppointmentDetail";
import { apiErrorMessage } from "../../shared/api-error";

// Mirrors CANCELLABLE_BOOKING_STATUSES on the api: once a booking has
// started it is closed by finishing it, not by cancelling it.
const CANCELLABLE_BOOKING_STATUSES = ["pending", "accepted"];

class MerchantAppointmentsList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      branches: [],
      selectedMerchant: "",
      selectedBranch: "",
      branchName: "",
      garageScoped: false,
      formOpen: false,
      editing: null,
      detailOf: null,
      globalFilter: "",
      startDateFilter: null,
      endDateFilter: null,
      canWrite: false,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");

    const canWrite = this.hasPermission([
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_WRITE,
    ]);
    this.setState({ canWrite });
    // Set the selectedMerchant directly from sessionStorage or fetch merchants list
    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, () => {
        this.fetchBranches();
        this.callPage();
      });
    } else {
      this.fetchMerchants();
    }

    const routeState = this.props.location.state || {};
    const { selectedBranch } = this.props.match.params;
    // opened from a garage record, so the garage is fixed for this page
    if (selectedBranch && selectedBranch !== ":selectedBranch") {
      this.setState(
        {
          selectedBranch,
          garageScoped: true,
          branchName: routeState.branchName || "",
          selectedMerchant:
            merchantIdFromStorage || routeState.selectedMerchant || "",
        },
        () => {
          if (this.state.selectedMerchant && !merchantIdFromStorage) {
            this.fetchBranches();
          }
          this.callPage();
        }
      );
    }
  }

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        merchants: response.data.items.map((merchant) => ({
          value: merchant.id,
          label: merchant.name,
        })),
      });
      if (this.state.merchants.length === 1) {
        this.setState({
          selectedMerchant: this.state.merchants[0].value,
        });
        this.fetchBranches();
      }
    }
  };

  fetchBranches = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) return;

    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/branches`
    );
    if (response && response.status === 200) {
      const branches = response.data.items.map((branch) => ({
        value: branch.id,
        label: branch.name || branch.address,
      }));
      this.setState((prev) => {
        const match = branches.find((b) => b.value === prev.selectedBranch);
        return {
          isLoading: false,
          branches,
          branchName: prev.branchName || (match ? match.label : ""),
        };
      });
    }
  };

  async callPage() {
    const { selectedBranch } = this.state;
    if (!selectedBranch || selectedBranch === ":selectedBranch") return;

    const pathname = `merchants-branches/${selectedBranch}/appointments`;
    const response = await this.engine.getItem(pathname);
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        dataSource: response.data.items.map((item, key) => ({
          id: key,
          data: item,
          date: item.date,
          fromToHours: item.fromToHours,
          slotsCount: item.slotsCount,
          appointmentThreshold: item.appointmentThreshold,
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    }
    this.props.loadingAction(false);
  }

  handlePage() {
    this.props.loadingAction(true);
    this.callPage();
  }

  async handleDelete(valueid) {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const { selectedBranch } = this.state;
      try {
        const result = await this.engine.deleteItem(
          `merchants-branches/${selectedBranch}/appointments`,
          valueid
        );
        if (result) {
          this.handlePage();
        }
      } catch (err) {
        // A booked slot is refused by the api — say why rather than looking
        // like the delete silently did nothing.
        alert(apiErrorMessage(err));
      }
      this.props.loadingAction(false);
    }
  }

  /**
   * Frees a booked slot. The appointment itself cannot be deleted while live
   * bookings hang off it, so this cancels the ones the garage may still turn
   * away — the api notifies each client, exactly as rejecting one from the
   * detail panel does. A booking already being worked on is left alone.
   */
  handleRejectBookings = async (item) => {
    const { selectedBranch } = this.state;

    this.props.loadingAction(true);
    let bookings = [];
    try {
      const response = await this.engine.getItem(
        `merchants-branches/${item.id}/appointment-booking`
      );
      const data = (response && response.data) || {};
      bookings = Array.isArray(data) ? data : data.items || [];
    } catch (err) {
      this.props.loadingAction(false);
      alert(apiErrorMessage(err));
      return;
    }
    this.props.loadingAction(false);

    // Same rule the detail panel follows: only a booking that has not been
    // started can be turned away.
    const open = bookings.filter((booking) =>
      CANCELLABLE_BOOKING_STATUSES.includes(`${booking.status}`.toLowerCase())
    );
    if (open.length === 0) {
      alert(strings.no_bookings_to_cancel);
      return;
    }
    if (
      !window.confirm(
        strings.formatString(strings.confirm_cancel_bookings, open.length)
      )
    ) {
      return;
    }
    // The client is told why, so the reason is not optional.
    const reason = window.prompt(strings.reject_reason_prompt);
    if (!reason) return;

    this.props.loadingAction(true);
    try {
      for (const booking of open) {
        await this.engine.patchItem(
          `merchants-branches/${selectedBranch}/bookings/${booking.id}/reject`,
          { statusReason: reason }
        );
      }
      this.handlePage();
    } catch (err) {
      alert(apiErrorMessage(err));
    }
    this.props.loadingAction(false);
  };

  handleActions(index, item) {
    // A slot can only be deleted once nothing live sits on it. While
    // something does, the row offers the way to free it instead of a delete
    // that would be refused.
    const blocking = item.blockingBookingsCount ?? 0;
    const cancellable = item.cancellableBookingsCount ?? 0;
    return (
      <RowActions
        actions={[
          {
            key: "detail",
            icon: "fa fa-car",
            label: strings.appointment_detail,
            onClick: () => this.setState({ detailOf: item }),
          },
          editAction(() => this.handleEdit(index, item.id), this.state.canWrite),
          cancellable > 0
            ? {
                key: "cancel-bookings",
                icon: "fa fa-ban",
                label: strings.cancel_bookings,
                danger: true,
                show: this.state.canWrite,
                onClick: () => this.handleRejectBookings(item),
              }
            : {
                ...deleteAction(
                  () => this.handleDelete(item.id),
                  this.state.canWrite
                ),
                // Nothing left to cancel, but something already served still
                // reads its date and time through this slot — the api refuses
                // to delete it, so say so rather than letting it be clicked.
                disabled: blocking > 0,
                label:
                  blocking > 0
                    ? strings.appointment_delete_booked
                    : strings.delete,
              },
        ]}
      />
    );
  }

  // Handle merchant selection change
  handleMerchantSelect = (event) => {
    const selectedMerchant = event.value;

    if (selectedMerchant !== this.state.selectedMerchant) {
      this.setState(
        { selectedMerchant, selectedBranch: "", branches: [], dataSource: [] },
        () => {
          this.fetchBranches();
        }
      );
    }
  };

  // Handle branch selection change
  handleBranchSelect = (event) => {
    const selectedBranch = event.value;
    this.setState({ selectedBranch }, () => {
      this.props.history.push(
        `/admin/merchants-branches/${selectedBranch}/appointments`
      );
      this.callPage();
    });
  };

  // add and edit open the same dialog over the list
  handleAdd() {
    const { selectedBranch } = this.state;

    if (!selectedBranch || selectedBranch === ":selectedBranch") {
      alert(strings.select + " " + strings.garage);
      return;
    }
    this.setState({ formOpen: true, editing: null });
  }

  handleEdit(index) {
    this.setState({
      formOpen: true,
      editing: this.state.dataSource[index].data,
    });
  }

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () => {
    this.setState({ formOpen: false, editing: null }, this.handlePage);
  };

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      branches,
      selectedMerchant,
      selectedBranch,
      branchName,
      garageScoped,
      formOpen,
      editing,
      detailOf,
      canWrite,
    } = this.state;
    let columns = [
      { Header: strings.fromToHours, accessor: "fromToHours" },
      { Header: strings.slotscount, accessor: "slotsCount" },
      {
        Header: strings.appointmentThreshold,
        accessor: "appointmentThreshold",
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <>
        <div className="content">
          <GarageSectionHeader
            branchId={garageScoped ? selectedBranch : ""}
            branchName={branchName}
            merchantId={selectedMerchant}
            active="appointments"
            history={this.props.history}
          />
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h4" className="pull-left">
                    {garageScoped ? strings.appointments : ""}
                  </CardTitle>
                  {canWrite && (
                    <Button
                      color="primary"
                      className="pull-right"
                      onClick={() => this.handleAdd()}
                    >
                      {strings.add}
                    </Button>
                  )}
                  {!garageScoped &&
                    !sessionStorage.getItem("merchantId") &&
                    merchants.length !== 1 && (
                      <SelectRow
                        label={strings.merchant}
                        name="selectedMerchant"
                        defaultValue={selectedMerchant}
                        data={merchants}
                        changeInput={(e) => this.handleMerchantSelect(e)}
                        placeholder={strings.select}
                      />
                    )}
                  {!garageScoped && (
                    <SelectRow
                      key={selectedMerchant || "default"}
                      label={strings.garage}
                      name="selectedBranch"
                      defaultValue={selectedBranch}
                      data={branches}
                      changeInput={(e) => this.handleBranchSelect(e)}
                      placeholder={strings.select}
                      viewonly={!selectedMerchant}
                    />
                  )}
                </CardHeader>
                <CardBody>
                  <ReactTable
                    previousText={strings.previous}
                    nextText={strings.next}
                    loadingText={strings.loading}
                    noDataText={strings.nodata}
                    pageText={strings.page}
                    data={dataSource}
                    defaultFilterMethod={this.filterCaseInsensitive}
                    filterable
                    loading={isLoading}
                    columns={columns}
                    defaultPageSize={10}
                    showPaginationTop={false}
                    showPaginationBottom
                    PaginationComponent={TablePagination}
                    className="-striped -highlight primary-pagination"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <FormModal
            isOpen={Boolean(detailOf)}
            onClose={() => this.setState({ detailOf: null })}
            icon="nc-icon nc-car"
            title={strings.appointment_detail}
            subtitle={
              detailOf ? `${detailOf.date} · ${detailOf.fromToHours}` : ""
            }
          >
            {detailOf && (
              <AppointmentDetail
                appointmentId={detailOf.id}
                branchId={selectedBranch}
                // Accepting or rejecting in here changes what the row may do,
                // so the list has to be re-read.
                onChanged={() => this.handlePage()}
                onStartService={(booking) =>
                  this.props.history.push(
                    `/admin/merchants-branches/${selectedBranch}/job-cards/new/${booking.id}`
                  )
                }
                onOpenWorkOrder={(row) =>
                  this.props.history.push({
                    pathname: `/admin/work-orders/${row.id}/manage`,
                    state: {
                      workOrder: { id: row.id },
                      booking: row.bookingId ? { id: row.bookingId } : null,
                    },
                  })
                }
              />
            )}
          </FormModal>

          <FormModal
            isOpen={formOpen}
            onClose={this.handleCloseForm}
            icon="nc-icon nc-calendar-60"
            size="md"
            title={
              editing
                ? `${strings.edit} ${strings.appointment}`
                : `${strings.add} ${strings.appointment}`
            }
            subtitle={branchName}
          >
            <AppointmentForm
              key={(editing && editing.id) || "new"}
              item={editing}
              branchId={selectedBranch}
              onSaved={this.handleSaved}
              onCancel={this.handleCloseForm}
            />
          </FormModal>
        </div>
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});
export default connect(null, mapDispatchToProps)(MerchantAppointmentsList);
